/**
 * Writing one message into the thread's cached pages, without a refetch.
 *
 * A write to a message used to be shown by awaiting its invalidation: the dialog that
 * ordered it stayed open until every mounted query had refetched, which is a visible wait
 * on a wide key set. Patching the cache gives the same correctness in the same frame, so
 * the refetch no longer has to be in the close path — it still runs, as the authority that
 * settles whatever a patch could not know.
 *
 * Pure: these take the cached value and hand back a new one. The query client lives in
 * `../_api/use-channel-message-cache.ts`.
 */

/**
 * A cached slice of a thread, in either shape the generated client stores it: the main
 * thread is an infinite list (`{ pages: [{ data }] }`); the pinned strip, search results
 * and a reply thread are single `.get()` pages (`{ data }`). All of them sit under the
 * same `getChannelsMessagesByCid({ cid })` key prefix, so a patch meets both.
 */
export type CachedMessages<TMessage> =
  | { pages: { data?: TMessage[] }[]; pageParams: unknown[] }
  | { data?: TMessage[] }
  | undefined;

/** What to do with the message. Returning `null` drops it from that cache. */
export type MessagePatch<TMessage> = (message: TMessage) => TMessage | null;

/** One page with the message patched (or dropped); the same page when it isn't here. */
function patchPage<
  TPage extends { data?: TMessage[] },
  TMessage extends { id: string },
>(page: TPage, messageId: string, patch: MessagePatch<TMessage>): TPage {
  if (!page.data?.some((message) => message.id === messageId)) return page;
  return {
    ...page,
    data: page.data
      .map((message) => (message.id === messageId ? patch(message) : message))
      .filter((message): message is TMessage => message != null),
  };
}

/**
 * The cached messages with one message patched or removed.
 *
 * Copy-on-write: pages and messages that do not hold it keep their identity, and a cache
 * that doesn't hold it at all comes back BY REFERENCE — which is how the caller tells
 * "nothing to write here" from "written", so nothing else in the thread re-renders.
 */
export function withPatchedMessage<TMessage extends { id: string }>(
  cached: CachedMessages<TMessage>,
  messageId: string,
  patch: MessagePatch<TMessage>,
): CachedMessages<TMessage> {
  if (!cached) return cached;
  if ("pages" in cached) {
    const pages = cached.pages.map((page) => patchPage(page, messageId, patch));
    if (pages.every((page, index) => page === cached.pages[index]))
      return cached;
    return { ...cached, pages };
  }
  return patchPage(cached, messageId, patch);
}

/**
 * Is this the pinned strip's query?
 *
 * The key is `[resource, "custom", path, params, query]`
 * (`packages/client/src/hooks/query-keys.ts`), so slot 4 is the query object — and the
 * strip is the one message query that sets `pinnedOnly` there
 * (`../_api/use-channel-messages.ts`). It matters because that list is filtered by the
 * SERVER: a message whose `pinnedAt` is cleared stays on it until the row is dropped.
 */
export function isPinnedOnlyMessagesKey(key: readonly unknown[]): boolean {
  const query = key[4];
  return (
    typeof query === "object" &&
    query !== null &&
    (query as { pinnedOnly?: unknown }).pinnedOnly === true
  );
}
